/**
 * 电子卡模块权限管理器
 * 根据电子卡模块权限API文档实现权限检查功能
 */

import api from '../api/api';

class EcardPermissionManager {
  constructor() {
    this.cache = new Map();
    this.cacheTime = 5 * 60 * 1000; // 5分钟缓存
  }

  /**
   * 获取用户绑定的电子卡模块标识
   * @param {string} onlinetoken - 在线token
   * @returns {Promise<Object>} 包含boundModuleMarks数组的权限信息
   */
  async getUserBoundModules(onlinetoken) {
    try {
      const response = await api.getUserEcardModulePermissions(onlinetoken);
      
      if (response.data.code === 200) {
        return {
          boundModuleMarks: response.data.data.boundModuleMarks || [],
          moduleDefinitions: response.data.data.moduleDefinitions || {},
          modulePermissions: response.data.data.modulePermissions || {}
        };
      }
      
      return { boundModuleMarks: [], moduleDefinitions: {}, modulePermissions: {} };
    } catch (error) {
      console.error('获取模块权限失败:', error);
      return { boundModuleMarks: [], moduleDefinitions: {}, modulePermissions: {} };
    }
  }

  /**
   * 检查用户是否有指定模块的权限
   * @param {string} onlinetoken - 在线token
   * @param {number} moduleMark - 模块标识（1-8）
   * @returns {Promise<boolean>} 是否有权限
   */
  async hasModulePermission(onlinetoken, moduleMark) {
    try {
      const data = await this.getUserBoundModules(onlinetoken);
      return data.boundModuleMarks.includes(moduleMark);
    } catch (error) {
      console.error('检查模块权限失败:', error);
      return false;
    }
  }

  /**
   * 获取用户所有模块的电子卡权限（保持向后兼容）
   * @param {string} onlinetoken - 在线token
   * @returns {Promise<Object>} 模块权限信息
   */
  async getUserModulePermissions(onlinetoken) {
    const data = await this.getUserBoundModules(onlinetoken);
    return data.modulePermissions;
  }

  /**
   * 检查特定模块权限（保持向后兼容）
   * @param {string} onlinetoken - 在线token
   * @param {number} moduleMark - 模块标识（1-8）
   * @returns {Promise<boolean>} 是否有权限
   */
  async checkModulePermission(onlinetoken, moduleMark) {
    return await this.hasModulePermission(onlinetoken, moduleMark);
  }

  /**
   * 检查新文科模块权限（模块标识8）
   * @param {string} onlinetoken - 在线token
   * @returns {Promise<boolean>} 是否有新文科模块权限
   */
  async hasXinWenKePermission(onlinetoken) {
    return await this.hasModulePermission(onlinetoken, 8);
  }

  /**
   * 检查互联网+模块权限（模块标识1）
   * @param {string} onlinetoken - 在线token
   * @returns {Promise<boolean>} 是否有互联网+模块权限
   */
  async hasInternetPlusPermission(onlinetoken) {
    return await this.hasModulePermission(onlinetoken, 1);
  }

  /**
   * 检查大挑模块权限（模块标识2）
   * @param {string} onlinetoken - 在线token
   * @returns {Promise<boolean>} 是否有大挑模块权限
   */
  async hasDaTiaoPermission(onlinetoken) {
    return await this.hasModulePermission(onlinetoken, 2);
  }

  /**
   * 检查小挑模块权限（模块标识3）
   * @param {string} onlinetoken - 在线token
   * @returns {Promise<boolean>} 是否有小挑模块权限
   */
  async hasXiaoTiaoPermission(onlinetoken) {
    return await this.hasModulePermission(onlinetoken, 3);
  }

  /**
   * 检查职规赛模块权限（模块标识4-7）
   * @param {string} onlinetoken - 在线token
   * @param {number} subModule - 子模块标识（4-7）
   * @returns {Promise<boolean>} 是否有职规赛模块权限
   */
  async hasCareerPlanningPermission(onlinetoken, subModule = 4) {
    if (subModule < 4 || subModule > 7) {
      subModule = 4; // 默认求职简历模块
    }
    return await this.hasModulePermission(onlinetoken, subModule);
  }

  /**
   * 带缓存的权限检查
   * @param {string} onlinetoken - 在线token
   * @param {boolean} forceRefresh - 是否强制刷新缓存
   * @returns {Promise<Object>} 权限信息
   */
  async getModulePermissionsWithCache(onlinetoken, forceRefresh = false) {
    const now = Date.now();
    const cacheKey = `modulePermissions_${onlinetoken}`;
    const cached = this.cache.get(cacheKey);
    
    if (!forceRefresh && cached && (now - cached.timestamp) < this.cacheTime) {
      return cached.data;
    }
    
    const permissions = await this.getUserBoundModules(onlinetoken);
    this.cache.set(cacheKey, {
      data: permissions,
      timestamp: now
    });
    
    return permissions;
  }

  /**
   * 清除缓存
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * 根据文件类型判断是否有对应模块权限
   * @param {string} onlinetoken - 在线token
   * @param {string} fileType - 文件类型标识
   * @returns {Promise<boolean>} 是否有权限
   */
  async hasPermissionForFileType(onlinetoken, fileType) {
    // 根据文件类型映射到对应的模块
    const typeModuleMap = {
      'xinwenke': 8,      // 新文科模块
      'internetplus': 1,  // 互联网+模块
      'datiao': 2,        // 大挑模块
      'xiaotiao': 3,      // 小挑模块
      'career': 4,        // 职规赛模块（默认求职简历）
      'career_report': 5, // 职规赛-生涯发展报告
      'career_ability': 6,// 职规赛-就业能力展示
      'career_show': 7    // 职规赛-生涯发展展示
    };

    const moduleMark = typeModuleMap[fileType];
    if (!moduleMark) {
      return false;
    }

    return await this.hasModulePermission(onlinetoken, moduleMark);
  }
}

// 创建单例实例
const permissionManager = new EcardPermissionManager();

export default permissionManager;